import React from 'react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';

const WatchSelector = ({ watches, loading, onSelect }) => {
  if (loading) return <p className="text-xs text-[#888] py-2">Loading watches...</p>;
  if (!watches.length) return null;

  return (
    <div className="space-y-2">
      <Label className="text-xs text-[#888] uppercase tracking-wider">Select a watch to inquire about (optional)</Label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[200px] overflow-y-auto pr-1">
        {watches.slice(0, 12).map((w) => (
          <button key={w.id} onClick={() => !w.sold_out && onSelect(w)} disabled={w.sold_out}
            className={`flex items-center gap-2 p-2 rounded-lg border text-left transition-colors ${w.sold_out ? 'border-[#2b2c2f] opacity-50 cursor-not-allowed' : 'border-[#2b2c2f] hover:border-[#D0B36B]/50 hover:bg-[#1a1a1f]'}`}
            data-testid={`watch-option-${w.id}`}>
            {w.image && <img src={w.image} alt={w.name} className={`w-8 h-8 rounded object-cover shrink-0 ${w.sold_out ? 'grayscale' : ''}`} />}
            <div className="min-w-0 flex-1">
              <p className="text-xs text-[#e5e5e5] truncate">{w.name}</p>
              {w.sold_out ? <p className="text-[10px] text-red-400 font-medium">SOLD OUT</p> : <p className="text-[10px] text-[#D0B36B]">${w.price?.toLocaleString()}</p>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const ContactForm = ({ name, email, firstMsg, selectedWatch, onNameChange, onEmailChange, onMsgChange, onSelectWatch, onDeselectWatch, onSubmit, starting, watches, watchesLoading }) => (
  <form onSubmit={onSubmit} className="space-y-4">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="space-y-1">
        <Label className="text-xs text-[#888] uppercase tracking-wider">Your Name</Label>
        <Input value={name} onChange={e => onNameChange(e.target.value)} placeholder="Your name" required data-testid="buyer-name" className="bg-[#1a1a1f] border-[#2b2c2f]" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-[#888] uppercase tracking-wider">Email</Label>
        <Input type="email" value={email} onChange={e => onEmailChange(e.target.value)} placeholder="you@email.com" required data-testid="buyer-email" className="bg-[#1a1a1f] border-[#2b2c2f]" />
      </div>
    </div>
    {selectedWatch ? (
      <div className="space-y-1">
        <Label className="text-xs text-[#888] uppercase tracking-wider">Inquiring about</Label>
        <div className="flex items-center gap-2 p-2 rounded-lg bg-[#1a1a1f] border border-[#D0B36B]/30">
          {selectedWatch.image && <img src={selectedWatch.image} alt={selectedWatch.name} className="w-10 h-10 rounded object-cover" />}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{selectedWatch.name}</p>
            <p className="text-xs text-[#D0B36B]">${selectedWatch.price?.toLocaleString()}</p>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={onDeselectWatch} className="text-xs text-[#888] shrink-0">Change</Button>
        </div>
      </div>
    ) : (
      <WatchSelector watches={watches} loading={watchesLoading} onSelect={onSelectWatch} />
    )}
    <div className="space-y-1">
      <Label className="text-xs text-[#888] uppercase tracking-wider">Your Message</Label>
      <Input value={firstMsg} onChange={e => onMsgChange(e.target.value)} placeholder="Hi, I'm interested in..." required data-testid="buyer-message" className="bg-[#1a1a1f] border-[#2b2c2f]" />
    </div>
    <Button type="submit" disabled={starting} className="w-full bg-[#D0B36B] text-black hover:bg-[#b89a55] font-medium" data-testid="start-chat-btn">
      {starting ? 'Starting...' : 'Start Conversation'}
    </Button>
  </form>
);

const ChatMessages = ({ messages, convStatus, interestCount, endRef }) => (
  <>
    {convStatus === 'sold_to_other' && <div className="px-4 py-2 bg-red-500/10 border-b border-red-500/20 text-center"><p className="text-xs text-red-400 font-medium">This watch has been sold.</p></div>}
    {convStatus === 'paid' && <div className="px-4 py-2 bg-green-500/10 border-b border-green-500/20 text-center"><p className="text-xs text-green-400 font-medium">Purchase confirmed!</p></div>}
    {convStatus === 'pending_payment' && <div className="px-4 py-2 bg-yellow-500/10 border-b border-yellow-500/20 text-center"><p className="text-xs text-yellow-400 font-medium">Awaiting your payment.</p></div>}
    {interestCount > 1 && convStatus === 'open' && <div className="px-4 py-2 bg-[#D0B36B]/10 border-b border-[#D0B36B]/20 text-center"><p className="text-xs text-[#D0B36B] font-medium">{interestCount} buyers are interested</p></div>}
    <div className="flex-1 overflow-y-auto px-4 py-4" data-testid="buyer-message-list">
      {messages.map((m) => {
        const isMe = m.sender_role === 'user';
        return (
          <div key={m.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'} mb-3`}>
            <div className="max-w-[80%]">
              <div className={`px-3 py-2 rounded-2xl text-sm ${isMe ? 'bg-[#D0B36B]/15 text-[#e5e5e5] rounded-br-md' : 'bg-[#1e1e24] text-[#e5e5e5] rounded-bl-md'}`}>{m.content}</div>
              {m.watch_ref && (
                <div className="flex items-center gap-2 p-2 rounded-lg bg-[#1a1a1f] border border-[#2b2c2f] mt-1 max-w-[260px]">
                  {m.watch_ref.image && <img src={m.watch_ref.image} alt={m.watch_ref.name} className="w-10 h-10 rounded object-cover shrink-0" />}
                  <div className="min-w-0"><p className="text-xs font-medium text-[#e5e5e5] truncate">{m.watch_ref.name}</p><p className="text-xs text-[#D0B36B]">${m.watch_ref.price?.toLocaleString()}</p></div>
                </div>
              )}
              <p className={`text-[10px] text-[#666] mt-0.5 ${isMe ? 'text-right' : ''}`}>{m.sender_name}</p>
            </div>
          </div>
        );
      })}
      <div ref={endRef} />
    </div>
  </>
);

export { WatchSelector, ContactForm, ChatMessages };
